import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Search, ArrowLeft, ShieldAlert, Filter,
  Heart, Lightbulb, AlertTriangle, Check, Archive, RotateCcw, Download,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SENTIMENTS = [
  { id: "all", label: "All", icon: MessageSquare, tone: "text-slate-300" },
  { id: "frustration", label: "Frustration", icon: AlertTriangle, tone: "text-footy-red" },
  { id: "idea", label: "Idea", icon: Lightbulb, tone: "text-amber-400" },
  { id: "praise", label: "Praise", icon: Heart, tone: "text-emerald-400" },
];

const STATES = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
  { id: "archived", label: "Archived" },
  { id: "all", label: "All" },
];

const sentimentTone = (s) => (SENTIMENTS.find(x => x.id === s) || SENTIMENTS[0]);

// Escape a cell for CSV — wrap in quotes if it contains comma/quote/newline.
const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentiment, setSentiment] = useState("all");
  const [state, setState] = useState("open");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feedback", { params: { sentiment, state } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load feedback");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sentiment, state]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(f =>
      (f.text || "").toLowerCase().includes(q) ||
      (f.user_email || "").toLowerCase().includes(q) ||
      (f.path || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const setItemState = async (id, newState) => {
    setBusyId(id);
    try {
      await api.post(`/admin/feedback/${id}/state`, { state: newState });
      setItems(prev => {
        // Optimistic update: if the current filter would exclude it, drop it from view
        if (state !== "all" && state !== newState) return prev.filter(f => f.id !== id);
        return prev.map(f => f.id === id ? { ...f, state: newState } : f);
      });
      toast.success(newState === "resolved" ? "Marked resolved" : newState === "archived" ? "Archived" : "Reopened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setBusyId(null); }
  };

  const exportCsv = () => {
    const header = ["ID", "Date", "Sentiment", "State", "User", "Page", "Message"];
    const rows = filtered.map(f => [
      f.id, f.at, f.sentiment, f.state,
      f.user_email || f.user_id || "",
      f.path || "",
      f.text || "",
    ]);
    const csv = [header, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `footymatch-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-footy-bg text-white flex items-center justify-center px-6">
        <div className="footy-card p-8 text-center max-w-sm">
          <ShieldAlert className="mx-auto text-footy-red mb-3" />
          <h3 className="font-display text-2xl uppercase">Admin only</h3>
          <p className="text-slate-400 mt-2 text-sm">This dashboard is restricted to moderators.</p>
          <Link to="/discover" className="footy-btn-primary mt-6 inline-flex">Back to app</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-footy-bg text-white grain">
      <header className="sticky top-0 z-30 bg-footy-bg/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/reports" className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="admin-feedback-back"><ArrowLeft size={20} /></Link>
            <h1 className="font-display text-3xl uppercase tracking-wider">Feedback</h1>
          </div>
          <button onClick={exportCsv} disabled={filtered.length === 0}
            className="footy-btn-secondary !py-2 !px-4 text-xs disabled:opacity-50"
            data-testid="feedback-export-csv" title="Export currently filtered rows to CSV">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 pt-6 pb-24">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e)=>setSearch(e.target.value)}
            placeholder="Search messages, users, pages..."
            className="footy-input pl-11" data-testid="feedback-search" />
        </div>

        {/* Sentiment filter */}
        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="feedback-sentiment-filter">
          <Filter size={14} className="text-slate-400" />
          {SENTIMENTS.map(s => {
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={()=>setSentiment(s.id)} data-testid={`feedback-sentiment-${s.id}`}
                className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                  sentiment === s.id ? "bg-footy-red border-footy-red text-white"
                  : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
                <Icon size={12} className={sentiment === s.id ? "text-white" : s.tone} />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* State filter */}
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="feedback-state-filter">
          {STATES.map(st => (
            <button key={st.id} onClick={()=>setState(st.id)} data-testid={`feedback-state-${st.id}`}
              className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                state === st.id ? "bg-white text-footy-bg border-white"
                : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
              {st.label}
            </button>
          ))}
          <span className="text-xs text-slate-500 ml-2" data-testid="feedback-count">
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>

        {/* List */}
        <div className="mt-6 space-y-3" data-testid="feedback-list">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)
          ) : filtered.length === 0 ? (
            <div className="footy-card p-10 text-center">
              <MessageSquare className="mx-auto text-slate-500 mb-3" />
              <h3 className="font-display text-2xl uppercase">No feedback</h3>
              <p className="text-slate-400 mt-2 text-sm">Nothing matches this filter yet.</p>
            </div>
          ) : filtered.map(f => (
            <FeedbackRow key={f.id} f={f} busyId={busyId} onSet={setItemState} />
          ))}
        </div>
      </div>
    </div>
  );
}

function FeedbackRow({ f, busyId, onSet }) {
  const s = sentimentTone(f.sentiment);
  const Icon = s.icon;
  return (
    <div className="footy-card p-5" data-testid={`feedback-${f.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg bg-white/5 flex-none flex items-center justify-center ${s.tone}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/10 border border-white/20">{s.label}</span>
            {f.state === "resolved" && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Resolved</span>}
            {f.state === "archived" && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-300 border border-slate-500/40">Archived</span>}
            <span className="text-xs text-slate-500 uppercase tracking-wider ml-auto">
              {new Date(f.at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <p className="text-sm text-slate-100 mt-2 whitespace-pre-wrap">{f.text}</p>
          <p className="text-[11px] text-slate-500 mt-2 truncate">
            {f.user_email || f.user_id || "anon"} · <span className="text-slate-400">{f.path || "/"}</span>
          </p>
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        {f.state !== "resolved" && (
          <button onClick={()=>onSet(f.id, "resolved")} disabled={busyId === f.id}
            className="footy-btn-secondary !py-2 !px-3 text-xs" data-testid={`feedback-resolve-${f.id}`}>
            <Check size={12} /> {busyId === f.id ? "..." : "Resolve"}
          </button>
        )}
        {f.state !== "archived" && (
          <button onClick={()=>onSet(f.id, "archived")} disabled={busyId === f.id}
            className="footy-btn-secondary !py-2 !px-3 text-xs" data-testid={`feedback-archive-${f.id}`}>
            <Archive size={12} /> Archive
          </button>
        )}
        {f.state !== "open" && (
          <button onClick={()=>onSet(f.id, "open")} disabled={busyId === f.id}
            className="text-xs text-slate-400 hover:text-white underline px-2" data-testid={`feedback-reopen-${f.id}`}>
            <RotateCcw size={12} className="inline mr-1" />
            Reopen
          </button>
        )}
      </div>
    </div>
  );
}
